import { List, Map, Set } from 'immutable';

import { allCategoriesLoaded, clearMenuTaxonomyAttrubutes, removeConditionFromMenu, updateMenuConditions, updateMenuTaxonomyAttrubutes } from '@/utils/bulkEdit/taxonomy';
import { getFilterIdsForMenuItem, getUpdatedMenuItemsFromOperations, shapeFiltersForAPI, updateFiltersForMenuItem } from '@/utils/bulkEdit';
import { setErrorCounts, shapeErrorCountsForApp, updateErrorCounts } from '@/utils/bulkEdit/errors';
import { getTemplateOperationsForProducts, shapeTemplateForApp } from '@/utils/profiles/templates';
import { getAIProfileOperations, getProductOperation } from '@/utils/bulkEdit/ai';
import { getAIRequest, shapeAIResponseForApp } from '@/utils/ai';
import { applyOperationsToProducts } from '@/utils/bulkEdit/operations';
import { shapeAiOperationsForApp } from '@/utils/profiles/ai';
import { applyPreviewOperations } from '@/utils/bulkEdit/preview/operations';
import { shapeOperationsForAPI } from '@/utils/bulkEdit/operations/shapeForAPI';
import { shapeListingsForApp } from '@/utils/profiles/listings';
import { shapeProductForApp } from '@/utils/product/shapeForApp';
import { updateListingScore } from '@/utils/product/score';
import { goBack, navigateTo } from '@/utils/navigation';
import { trackAiOperations } from '@/utils/bulkEdit/tracking';
import { getPreviewActions } from '@/utils/bulkEdit/preview';
import { getImageTypeByUrl } from '@/utils/photos';
import { filterOperations } from '@/utils/product/operations';
import { setProductErrors } from '@/utils/product/errors';
import { createController } from '@/utils/controller';
import { getListingsPath } from '@/utils/listings/getListingsPath';
import { isApplyDisabled } from '@/utils/bulkEdit/isApplyDisabled';
import { getPageInfo } from '@/utils/pagination';
import { isPlusPlan } from '@/utils/billing';
import { areEqual } from '@/utils/iterable/areEqual';
import { isTruthy } from '@/utils/bool';
import { shapeId } from '@/utils/listings/listings';
import { getSize } from '@/utils/iterable/getSize';
import { keepOld } from '@/utils/iterable/keepOld';
import { posthog } from '@/utils/tracking/posthog';
import { api } from '@/utils/api';

import { FILTER, MENU_ITEM, MENU_ITEMS, MENU_ITEMS_TO_ATTRIBUTE_MAP, MENU_SECTION, MENU_SECTIONS, MENU_SECTION_ITEMS, OPERATIONS, SAVE_TYPE } from '@/constants/bulkEdit';
import { AI_ATTRIBUTE_EVENT, AI_OPERATION, DESTINATION_TO_ATTRIBUTE } from '@/constants/ai';
import { SYNC_INDICATOR, POLLING_INTERVAL } from '@/constants/shops';
import { AI, AMOUNT, ERROR, NEW, PRICE_API_KEY_TO_ATTRIBUTE_CURRENCY_MAP, VALUE } from '@/constants/product';
import { PAGE_SIZE, SELECTION, STATUS } from '@/constants/listings';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { MESSAGE, NOTIFICATION } from '@/constants/notifications';
import { MAX_NUMBER_OF_AI_TAGS } from '@/constants/validations';
import { PROFILE_TYPE } from '@/constants/profiles';
import { EMPTY, VELA } from '@/constants';
import { ATTRIBUTE } from '@/constants/attributes';
import { FEATURE } from '@/constants/features';
import { MODALS } from '@/constants/modal';
import { FORM } from '@/constants/forms';

import actions from '@/actions';

const categoriesController = createController();
const invalidController = createController();
const listingsController = createController();
const listingIdsController = createController();
const profileController = createController();
const profileListingsController = createController();

function getDefaultOperation({ operations, plan }) {
  if (!operations || getSize(operations) === 0) return undefined;

  if (!isPlusPlan(plan)) {
    for (let i = 0; i < getSize(operations); i++) {
      const operation = operations.get(i);

      if (operation && !/aiGenerated/.test(operation)) {
        return operation;
      }
    }
  }
  return operations.get(0);
}

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    categoriesController,
    invalidController,
    listingsController,
    listingIdsController,
    profileController,
    profileListingsController,
  ].forEach(abort);
}

function* addOperation(reduction, operation) {
  function updateEdited(edited = EMPTY.SET) {
    return edited.union(operation.get('products'));
  }

  function updateOperations(operations = EMPTY.LIST) {
    return operations.push(operation);
  }

  return reduction
    .updateIn(['bulkEdit', 'operations'], updateOperations)
    .updateIn(['bulkEdit', 'products', 'edited'], updateEdited);
}

function* addPendingUpdates(reduction, section) {
  const menuItem = section || reduction.getIn(['bulkEdit', 'menu', 'selected', 'item']);

  function updatePendingUpdates(pendingUpdates = EMPTY.SET) {
    return pendingUpdates.has(menuItem)
      ? pendingUpdates
      : pendingUpdates.add(menuItem);
  }

  return reduction.updateIn(['bulkEdit', 'menu', 'pendingUpdates'], updatePendingUpdates);
}

function* addPreview(reduction, attributes) {
  const userFeatures = reduction.getIn(['user', 'features']);
  let bulkEdit = reduction.get('bulkEdit');
  const channel = bulkEdit.get('channel');
  const preview = bulkEdit.get('preview', EMPTY.MAP).merge(attributes);
  bulkEdit = bulkEdit.set('preview', preview);

  yield function effect(dispatch) {
    const previewActions = getPreviewActions({ channel, preview });

    if (getSize(previewActions) > 0) {
      previewActions.forEach(dispatch);
    }
  };

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* aiDiscard(reduction, { attribute }) {
  const userFeatures = reduction.getIn(['user', 'features']);
  let bulkEdit = reduction.get('bulkEdit');
  const page = bulkEdit.getIn(['page', 'currentPage']);
  const preview = bulkEdit.getIn(['preview', attribute], EMPTY.MAP);

  const values = preview
    .get(AI, EMPTY.MAP)
    .deleteAll(
      preview
        .getIn(['products', 'processed', page], EMPTY.SET)
        .union(preview.getIn(['products', 'processing', page], EMPTY.SET))
    );

  const processed = preview.getIn(['products', 'processed'], EMPTY.MAP).delete(page);
  const processing = preview.getIn(['products', 'processing'], EMPTY.MAP).delete(page);

  if (getSize(values) > 0) {
    bulkEdit = bulkEdit.setIn(['preview', attribute, AI], values);
  } else {
    bulkEdit = bulkEdit.deleteIn(['preview', attribute, AI]);
  }

  if (getSize(processed) > 0) {
    bulkEdit = bulkEdit.setIn(['preview', attribute, 'products', 'processed'], processed);
  } else {
    bulkEdit = bulkEdit.deleteIn(['preview', attribute, 'products', 'processed']);
  }

  if (getSize(processing) > 0) {
    bulkEdit = bulkEdit.setIn(['preview', attribute, 'products', 'processing'], processing);
  } else {
    bulkEdit = bulkEdit.deleteIn(['preview', attribute, 'products', 'processing']);
  }

  if (
    bulkEdit.hasIn(['preview', attribute, 'products']) &&
    getSize(bulkEdit.getIn(['preview', attribute, 'products'])) === 0
  ) {
    bulkEdit = bulkEdit.deleteIn(['preview', attribute, 'products']);
  }

  if (!bulkEdit.hasIn(['preview', attribute, AI]) && !bulkEdit.hasIn(['preview', attribute, 'products'])) {
    bulkEdit = bulkEdit.set('preview', EMPTY.MAP);
  }

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* aiOperationGenerate(state, { operation, product: initial }) {
  yield function effect(dispatch) {
    const bulkEdit = state.get('bulkEdit');
    const shopId = bulkEdit.get('shopId');
    const menu = bulkEdit.get('menu');
    const destination = operation.getIn(['value', 'destination_field']);
    const attribute = DESTINATION_TO_ATTRIBUTE[destination] || destination;
    let product = initial;

    switch (attribute) {
      case ATTRIBUTE.DESCRIPTION: {
        if (menu.getIn(['selected', 'item']) !== MENU_ITEM.DESCRIPTION) return;

        product = product
          .setIn([FORM.DESCRIPTION, AI, 'instructions'], operation.getIn(['value', 'input', 'instructions']))
          .setIn([FORM.DESCRIPTION, AI, 'operation'], operation.getIn(['value', 'operation']));

        posthog.track({
          additional_instructions: !!operation.getIn(['value', 'input', 'instructions']),
          event: AI_ATTRIBUTE_EVENT[ATTRIBUTE.DESCRIPTION].OPTIMIZE,
          listings_count: 1,
          page: 'bulk_edit',
          shopId,
          source_component: 'attribute_optimization',
        });

        break;
      }

      case ATTRIBUTE.META_DESCRIPTION: {
        if (menu.getIn(['selected', 'item']) !== MENU_ITEM.META_DESCRIPTION) return;

        product = product
          .setIn([FORM.SEO, ATTRIBUTE.META_DESCRIPTION, AI, 'instructions'],
            operation.getIn(['value', 'input', 'instructions'])
          )
          .setIn([FORM.SEO, ATTRIBUTE.META_DESCRIPTION, AI, 'operation'],
            operation.getIn(['value', 'operation'])
          );

        posthog.track({
          additional_instructions: !!operation.getIn(['value', 'input', 'instructions']),
          event: AI_ATTRIBUTE_EVENT[ATTRIBUTE.META_DESCRIPTION].OPTIMIZE,
          listings_count: 1,
          page: 'bulk_edit',
          shopId,
          source_component: 'attribute_optimization',
        });

        break;
      }

      case ATTRIBUTE.PAGE_TITLE: {
        if (menu.getIn(['selected', 'item']) !== MENU_ITEM.PAGE_TITLE) return;

        product = product
          .setIn([FORM.SEO, ATTRIBUTE.PAGE_TITLE, AI, 'instructions'],
            operation.getIn(['value', 'input', 'instructions'])
          )
          .setIn([FORM.SEO, ATTRIBUTE.PAGE_TITLE, AI, 'operation'],
            operation.getIn(['value', 'operation'])
          );

        posthog.track({
          additional_instructions: !!operation.getIn(['value', 'input', 'instructions']),
          event: AI_ATTRIBUTE_EVENT[ATTRIBUTE.PAGE_TITLE].OPTIMIZE,
          listings_count: 1,
          page: 'bulk_edit',
          shopId,
          source_component: 'attribute_optimization',
        });

        break;
      }

      case ATTRIBUTE.TAGS: {
        if (menu.getIn(['selected', 'item']) !== MENU_ITEM.TAGS) return;

        product = product
          .setIn([FORM.TAGS, AI, 'instructions'], operation.getIn(['value', 'input', 'instructions']))
          .setIn([FORM.TAGS, AI, 'operation'], operation.getIn(['value', 'operation']));

        posthog.track({
          additional_instructions: !!operation.getIn(['value', 'input', 'instructions']),
          event: AI_ATTRIBUTE_EVENT[ATTRIBUTE.TAGS][operation.getIn(['value', 'operation'])],
          listings_count: 1,
          page: 'bulk_edit',
          shopId,
          source_component: 'attribute_optimization',
        });

        break;
      }

      case ATTRIBUTE.TITLE: {
        if (menu.getIn(['selected', 'item']) !== MENU_ITEM.TITLE) return;

        product = product
          .setIn([FORM.TITLE, AI, 'instructions'], operation.getIn(['value', 'input', 'instructions']))
          .setIn([FORM.TITLE, AI, 'operation'], operation.getIn(['value', 'operation']));

        posthog.track({
          additional_instructions: !!operation.getIn(['value', 'input', 'instructions']),
          event: AI_ATTRIBUTE_EVENT[ATTRIBUTE.TITLE].OPTIMIZE,
          listings_count: 1,
          page: 'bulk_edit',
          shopId,
          source_component: 'attribute_optimization',
        });

        break;
      }

      default: {
        break;
      }
    }

    const shop = state.getIn(['shops', 'byId', shopId]);
    const {
      request: {
        channel,
        input,
        key,
        operation: type,
        db,
      },
      state: ai,
    } = getAIRequest({
      attribute,
      product,
      shop,
    });

    const controller = createController();
    const { signal } = controller.start();
    const path = ['generating', attribute, shopId, key];

    if (state.hasIn(['data', ...path])) {
      abort(state.getIn(['data', ...path]));
    }

    dispatch(actions.data.setData({ path, value: controller }));

    api.ai.generateText({ channel, db, destination, input, operation: type, shopId, signal })
      .then(
        (response) => dispatch(actions.bulkEdit.aiOperationGenerate.fulfilled({ ai, operation, key, response })),
        (error) => dispatch(actions.bulkEdit.aiOperationGenerate.rejected({ attribute, error, key, signal })),
      );
  };

  return state;
}

function* aiOperationGenerateFulfilled(state, { ai, operation, key: productId, response }) {
  let bulkEdit = state.get('bulkEdit');
  const channel = bulkEdit.get('channel');
  const shopId = bulkEdit.get('shopId');
  const destination = operation.getIn(['value', 'destination_field']);
  const attribute = DESTINATION_TO_ATTRIBUTE[destination] || destination;
  const path = ['generating', attribute, shopId, productId];
  const shopData = state.getIn(['data', 'shopsData', shopId]);
  const shop = state.getIn(['shops', 'byId', shopId]);
  const userFeatures = state.getIn(['user', 'features']);

  if (state.hasIn(['data', ...path])) {
    abort(state.getIn(['data', ...path]));

    yield function effect(dispatch) {
      dispatch(actions.data.setData({ path }));
    };
  }

  if (!bulkEdit.hasIn(['products', 'byId', productId])) return state;

  function findOperation(item) {
    return (
      item.get('type') === operation.get('type') &&
      item.get('value').equals(operation.get('value')) &&
      item.get('products').includes(productId)
    );
  }

  let operations = bulkEdit.get('operations', EMPTY.LIST);
  const operationIndex = operations.findIndex(findOperation);

  if (operationIndex === -1) return state;

  function updateOperationProducts(operationProducts) {
    function filterOperationProducts(item) {
      return item !== productId;
    }

    return operationProducts.filter(filterOperationProducts);
  }

  if (getSize(operations.getIn([operationIndex, 'products'])) > 1) {
    operations = operations.updateIn([operationIndex, 'products'], updateOperationProducts);

    const operationProductIds = operations.getIn([operationIndex, 'products']);
    const pageProductIds = bulkEdit.getIn(['products', 'ids']);

    if (getSize(operationProductIds.toSet().intersect(pageProductIds.toSet())) === 0) {
      bulkEdit = bulkEdit.delete('processing');
    }
  } else {
    bulkEdit = bulkEdit.delete('processing');
    operations = operations.splice(operationIndex, 1);
  }

  const productOperation = getProductOperation({
    ai: ai.set('generated', shapeAIResponseForApp({ ai, attribute, channel, response })),
    attribute,
    channel,
    productId,
  });

  bulkEdit = bulkEdit.set('operations', operations.push(productOperation));

  const updates = applyOperationsToProducts({
    operations: List([productOperation]),
    products: bulkEdit.get('products'),
    shop,
    shopData,
    userFeatures,
  });

  bulkEdit = bulkEdit.set('products', updates.products);

  if (!bulkEdit.get('vela')) {
    bulkEdit = bulkEdit.setIn(['errors', 'counts'],
      setErrorCounts({ channel, counts: EMPTY.MAP, products: bulkEdit.get('products') })
    );
  }

  return state.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* aiOperationGenerateRejected(state, { attribute, error, key, signal }) {
  let bulkEdit = state.get('bulkEdit');

  if (signal.aborted) return state;

  if (error) {
    console.error(error);
  }

  const shopId = bulkEdit.get('shopId');
  const path = ['generating', attribute, shopId, key];
  const userFeatures = state.getIn(['user', 'features']);

  yield function effect(dispatch) {
    dispatch(actions.data.setData({ path }));

    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.GENERATE,
        type: NOTIFICATION.ERROR,
      })
    );
  };

  if (getSize(state.getIn(['data', 'generating', attribute, shopId], EMPTY.MAP).deleteAll([VELA, key])) === 0) {
    bulkEdit = bulkEdit.delete('processing');
  }

  return state.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* aiPreviewGenerate(reduction, { attribute }) {
  let bulkEdit = reduction.get('bulkEdit');
  const db = bulkEdit.get('db');
  const channel = bulkEdit.get('channel');
  const shopId = bulkEdit.get('shopId');
  const page = bulkEdit.getIn(['page', 'currentPage']);
  const preview = bulkEdit.get('preview', EMPTY.MAP);
  const products = bulkEdit.get('products', EMPTY.MAP);
  const productIds = products.get('ids', EMPTY.LIST);
  const shop = reduction.getIn(['shops', 'byId', shopId]);
  const userFeatures = reduction.getIn(['user', 'features']);

  function reduceProductIds(result, productId) {
    if (!products.hasIn(['selected', productId])) return result;

    const product = products.getIn(['byId', productId]);

    switch (attribute) {
      case ATTRIBUTE.DESCRIPTION: {
        if (!product.getIn([FORM.TITLE, VALUE])) return result;

        result.ai = result.ai
          .setIn([productId, 'instructions'], result.ai.getIn([VELA, 'instructions']))
          .setIn([productId, 'operation'], result.ai.getIn([VELA, 'operation']));

        const { request: { destination, input, operation }, state: ai } = getAIRequest({
          attribute,
          product: product.setIn([FORM.DESCRIPTION, AI], result.ai.get(productId)),
          shop,
        });

        result.ai = result.ai.set(productId, ai);
        result.processing = result.processing.add(productId);
        result.requests.push({ channel, destination, input, operation, productId });
        result.total += 1;
        break;
      }

      case ATTRIBUTE.META_DESCRIPTION: {
        if (!product.getIn([FORM.TITLE, VALUE])) return result;

        result.ai = result.ai
          .setIn([productId, 'instructions'], result.ai.getIn([VELA, 'instructions']))
          .setIn([productId, 'operation'], result.ai.getIn([VELA, 'operation']));

        const { request: { destination, input, operation }, state: ai } = getAIRequest({
          attribute,
          product: product.setIn([FORM.SEO, AI, ATTRIBUTE.META_DESCRIPTION], result.ai.get(productId)),
          shop,
        });

        result.ai = result.ai.set(productId, ai);
        result.processing = result.processing.add(productId);
        result.requests.push({ channel, destination, input, operation, productId });
        result.total += 1;
        break;
      }

      case ATTRIBUTE.PAGE_TITLE: {
        if (!product.getIn([FORM.TITLE, VALUE])) return result;

        result.ai = result.ai
          .setIn([productId, 'instructions'], result.ai.getIn([VELA, 'instructions']))
          .setIn([productId, 'operation'], result.ai.getIn([VELA, 'operation']));

        const { request: { destination, input, operation }, state: ai } = getAIRequest({
          attribute,
          product: product.setIn([FORM.SEO, AI, ATTRIBUTE.PAGE_TITLE], result.ai.get(productId)),
          shop,
        });

        result.ai = result.ai.set(productId, ai);
        result.processing = result.processing.add(productId);
        result.requests.push({ channel, destination, input, operation, productId });
        result.total += 1;
        break;
      }

      case ATTRIBUTE.PHOTOS: {
        const photos = product.getIn([FORM.PHOTOS, VALUE], EMPTY.LIST);

        preview.getIn([attribute, 'indexes']).forEach(function forEachIndex(index) {
          const url = photos.getIn([index, 'fullsize_url']);
          const imageType = getImageTypeByUrl(url);

          if (imageType && imageType !== 'local') {
            if (!result.ai.has(productId)) {
              result.ai = result.ai.set(productId, EMPTY.MAP);
              result.total += 1;
            }

            if (!getSize(result.processing)) {
              result.processing = Map({ [productId]: Set([index]) });
            } else if (!result.processing.has(productId)) {
              result.processing = result.processing.set(productId, Set([index]));
            } else {
              result.processing = result.processing.update(productId, function updateProcessing(processing) {
                return processing.add(index);
              });
            }

            result.requests.push({ image: url, index, productId });
          }
        });

        break;
      }

      case ATTRIBUTE.TAGS: {
        switch (result.ai.getIn([VELA, 'operation'])) {
          case AI_OPERATION.ADD_MAXIMUM: {
            if (
              !product.getIn([FORM.TITLE, VALUE]) || (
                MAX_NUMBER_OF_AI_TAGS.hasOwnProperty(channel) &&
                getSize(product.getIn([FORM.TAGS, VALUE]).mergeWith(keepOld, product.getIn([FORM.TAGS, NEW]))) >=
                MAX_NUMBER_OF_AI_TAGS[channel]
              )
            ) {
              return result;
            }

            break;
          }

          default: {
            if (!product.getIn([FORM.TITLE, VALUE])) {
              return result;
            }

            break;
          }
        }

        result.ai = result.ai
          .setIn([productId, 'instructions'], result.ai.getIn([VELA, 'instructions']))
          .setIn([productId, 'operation'], result.ai.getIn([VELA, 'operation']));

        const { request: { destination, input, operation }, state: ai } = getAIRequest({
          attribute,
          product: product.setIn([FORM.TAGS, AI], result.ai.get(productId)),
          shop,
        });

        result.ai = result.ai.set(productId, ai);
        result.processing = result.processing.add(productId);
        result.requests.push({ channel, destination, input, operation, productId });
        result.total += 1;
        break;
      }

      case ATTRIBUTE.TITLE: {
        if (!product.getIn([FORM.TITLE, VALUE])) return result;

        result.ai = result.ai
          .setIn([productId, 'instructions'], result.ai.getIn([VELA, 'instructions']))
          .setIn([productId, 'operation'], result.ai.getIn([VELA, 'operation']));

        const { request: { channel: requestChannel, destination, input, operation }, state: ai } = getAIRequest({
          attribute,
          product: product.setIn([FORM.TITLE, AI], result.ai.get(productId)),
          shop,
        });

        result.ai = result.ai.set(productId, ai);
        result.processing = result.processing.add(productId);
        result.requests.push({ channel: requestChannel, destination, input, operation, productId });
        result.total += 1;
        break;
      }

      default: {
        break;
      }
    }

    return result;
  }

  const { ai, processing, requests, total } = productIds.reduce(
    reduceProductIds,
    {
      ai: preview.getIn([attribute, AI], EMPTY.MAP),
      processing: preview.getIn([attribute, 'products', 'processing', page], EMPTY.SET),
      requests: [],
      total: 0,
    },
  );

  bulkEdit = bulkEdit.setIn(['preview', attribute, AI], ai);

  if (getSize(requests) > 0) {
    yield function effect(dispatch) {
      function forEachRequest({ productId, ...request }) {
        const controller = createController();
        const { signal } = controller.start();
        const key = attribute === ATTRIBUTE.PHOTOS ? ATTRIBUTE.ALT_TEXT : attribute;
        const path = ['generating', key, shopId, productId];
        dispatch(actions.data.setData({ path, value: controller }));

        function onError(error) {
          if (signal.aborted) return;

          dispatch(
            actions.bulkEdit.aiPreviewGenerate.rejected({
              attribute,
              error,
              page,
              path,
              productId,
              request,
              signal,
            })
          );
        }

        function onResponse(response) {
          dispatch(
            actions.bulkEdit.aiPreviewGenerate.fulfilled({
              attribute,
              page,
              path,
              productId,
              request,
              response,
            })
          );
        }

        switch (attribute) {
          case ATTRIBUTE.PHOTOS: {
            api.ai.generateAltText({ ...request, signal, shopId, db, channel }).then(onResponse, onError);
            break;
          }

          default: {
            api.ai.generateText({ ...request, signal, shopId, db }).then(onResponse, onError);
            break;
          }
        }
      }

      requests.forEach(forEachRequest);

      switch (attribute) {
        case ATTRIBUTE.TAGS: {
          posthog.track({
            additional_instructions: !!bulkEdit.getIn(['preview', attribute, AI, VELA, 'instructions']),
            event: bulkEdit.getIn(['preview', attribute, 'retry'])
              ? AI_ATTRIBUTE_EVENT[attribute].RETRY
              : AI_ATTRIBUTE_EVENT[attribute][bulkEdit.getIn(['preview', ATTRIBUTE.TAGS, AI, VELA, 'operation'])],
            listings_count: total,
            page: 'bulk_edit',
            shopId,
            source_component: 'attribute_optimization',
          });

          break;
        }

        default: {
          posthog.track({
            additional_instructions: !!bulkEdit.getIn(['preview', attribute, AI, VELA, 'instructions']),
            event: bulkEdit.getIn(['preview', attribute, 'retry'])
              ? AI_ATTRIBUTE_EVENT[attribute].RETRY
              : AI_ATTRIBUTE_EVENT[attribute].OPTIMIZE,
            listings_count: total,
            page: 'bulk_edit',
            shopId,
            source_component: 'attribute_optimization',
          });

          break;
        }
      }
    };
  }

  if (getSize(processing)) {
    bulkEdit = bulkEdit.setIn(['preview', attribute, 'products', 'processing', page], processing);
  } else {
    bulkEdit = bulkEdit.deleteIn(['preview', attribute, 'products', 'processing', page]);

    if (
      bulkEdit.hasIn(['preview', attribute, 'products', 'processing']) &&
      getSize(bulkEdit.getIn(['preview', attribute, 'products', 'processing'])) === 0
    ) {
      bulkEdit = bulkEdit.deleteIn(['preview', attribute, 'products', 'processing']);
    }
  }

  if (
    bulkEdit.hasIn(['preview', attribute, 'products', 'processed']) &&
    getSize(bulkEdit.getIn(['preview', attribute, 'products', 'processed'])) === 0
  ) {
    bulkEdit = bulkEdit.deleteIn(['preview', attribute, 'products', 'processed']);
  }

  if (
    bulkEdit.hasIn(['preview', attribute, 'products']) &&
    getSize(bulkEdit.getIn(['preview', attribute, 'products'])) === 0
  ) {
    bulkEdit = bulkEdit.deleteIn(['preview', attribute, 'products']);
  }

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* aiPreviewGenerateFulfilled(reduction, { attribute, page, path, productId, request, response }) {
  let bulkEdit = reduction.get('bulkEdit');
  const channel = bulkEdit.get('channel');
  const controller = reduction.getIn(['data', ...path]);
  const userFeatures = reduction.getIn(['user', 'features']);

  if (controller) {
    abort(controller);

    yield function effect(dispatch) {
      dispatch(actions.data.setData({ path }));
    };
  }

  function updatePreview(source = EMPTY.MAP) {
    function updateProcessing(processing) {
      switch (attribute) {
        case ATTRIBUTE.PHOTOS: {
          const updated = (processing || EMPTY.MAP).deleteIn([productId, request.index]);
          return getSize(updated.get(productId))
            ? updated
            : updated.delete(productId);
        }

        default: {
          return (processing || EMPTY.SET).delete(productId);
        }
      }
    }

    function updateProcessed(processed) {
      switch (attribute) {
        case ATTRIBUTE.PHOTOS: {
          return (processed || EMPTY.MAP).update(productId, function updateProcessedIndexes(indexes = EMPTY.SET) {
            return indexes.add(request.index);
          });
        }

        default: {
          return (processed || EMPTY.SET).add(productId);
        }
      }
    }

    function updateValue(value = EMPTY.MAP) {
      switch (attribute) {
        case ATTRIBUTE.PHOTOS: {
          return value.setIn(['generated', request.index], response.altText);
        }

        default: {
          return value.set('generated', shapeAIResponseForApp({ ai: value, attribute, channel, response }));
        }
      }
    }

    let preview = source
      .updateIn([attribute, 'products', 'processed', page], updateProcessed)
      .updateIn([attribute, 'products', 'processing', page], updateProcessing)
      .updateIn([attribute, AI, productId], updateValue);

    if (
      preview.hasIn([attribute, 'products', 'processing', page]) &&
      getSize(preview.getIn([attribute, 'products', 'processing', page])) === 0
    ) {
      preview = preview.deleteIn([attribute, 'products', 'processing', page]);
    }

    if (
      preview.hasIn([attribute, 'products', 'processing']) &&
      getSize(preview.getIn([attribute, 'products', 'processing'])) === 0
    ) {
      preview = preview.deleteIn([attribute, 'products', 'processing']);
    }

    return preview;
  }

  bulkEdit = bulkEdit.update('preview', updatePreview);

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* aiPreviewGenerateRejected(reduction, { attribute, error, page, path, productId, request, signal }) {
  if (signal.aborted) return reduction;

  const controller = reduction.getIn(['data', ...path]);

  if (controller) {
    abort(controller);

    yield function effect(dispatch) {
      dispatch(actions.data.setData({ path }));
    };
  }

  if (error) {
    console.error(error);
  }

  let bulkEdit = reduction.get('bulkEdit');
  const userFeatures = reduction.getIn(['user', 'features']);

  function updatePreview(source = EMPTY.MAP) {
    let preview = source;

    switch (attribute) {
      case ATTRIBUTE.PHOTOS: {
        preview = preview.deleteIn(['products', 'processing', page, productId, request.index]);

        if (getSize(preview.getIn(['products', 'processing', page, productId])) === 0) {
          preview = preview.deleteIn(['products', 'processing', page, productId]);
        }

        break;
      }

      default: {
        preview = preview.deleteIn(['products', 'processing', page, productId]);
        break;
      }
    }

    if (
      preview.hasIn(['products', 'processing']) &&
      getSize(preview.getIn(['products', 'processing'])) === 0
    ) {
      preview = preview.deleteIn(['products', 'processing']);
    }

    if (
      preview.has('products') &&
      getSize(preview.get('products')) === 0
    ) {
      preview = preview.delete('products');
    }

    return preview;
  }

  bulkEdit = bulkEdit.updateIn(['preview', attribute], updatePreview);

  if (getSize(bulkEdit.getIn(['preview', attribute, 'products', 'processing'])) === 0) {
    yield function effect(dispatch) {
      dispatch(
        actions.notifications.add({
          message: MESSAGE.FAIL.GENERATE,
          type: NOTIFICATION.ERROR,
        })
      );
    };
  }

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* applyPreview(reduction) {
  if (
    getSize(reduction.get('bulkEdit')) === 0 || (
      getSize(reduction.getIn(['bulkEdit', 'preview']))
        ? reduction.getIn(['bulkEdit', 'applyDisabled'])
        : getSize(reduction.getIn(['bulkEdit', 'operations'])) === 0
    )
  ) {
    return reduction;
  }

  let previewOperations = EMPTY.LIST;
  let bulkEdit = reduction.get('bulkEdit');
  let operations = bulkEdit.get('operations', EMPTY.LIST);
  const shopId = bulkEdit.get('shopId');
  const channel = bulkEdit.get('channel');
  const shop = reduction.getIn(['shops', 'byId', shopId]);
  const shopData = reduction.getIn(['data', 'shopsData', shopId]);
  const userFeatures = reduction.getIn(['user', 'features']);
  const {
    actions: previewActions,
    edited = bulkEdit.getIn(['products', 'selected']),
    payload,
    preview = EMPTY.MAP,
  } = applyPreviewOperations({ bulkEdit, userFeatures: reduction.getIn(['user', 'features'], EMPTY.MAP) });

  const selectedMenuItem = bulkEdit.getIn(['menu', 'selected', 'item']);

  function makeOperation(operation) {
    if (
      (
        !operation.has('value') &&
        !operation.has('values') &&
        selectedMenuItem &&
        !operation.has(selectedMenuItem)
      ) ||
      !operation.has('type')
    ) {
      return undefined;
    }

    return operation.set('products', edited.toList());
  }

  if (getSize(previewActions)) {
    yield function effect(dispatch) {
      previewActions.forEach(dispatch);
    };
  }

  if (Array.isArray(payload)) {
    previewOperations = List(payload.map(makeOperation).filter(isTruthy));
  } else if (Map.isMap(payload)) {
    previewOperations = previewOperations.push(makeOperation(payload)).filter(isTruthy);
  } else if (List.isList(payload)) {
    previewOperations = payload;
  }

  operations = operations.concat(previewOperations);

  if (!getSize(operations)) {
    return reduction;
  }

  function updateEdited(oldEdited = EMPTY.SET) {
    return oldEdited.union(edited);
  }

  yield function effect(dispatch) {
    dispatch(actions.bulkEdit.addPendingUpdates());
  };

  const { actions: operationActions, confirmation, products } = applyOperationsToProducts({
    operations: previewOperations,
    products: bulkEdit.get('products').update('edited', updateEdited),
    shop,
    shopData,
    userFeatures,
  });

  if (channel === FAIRE) {
    const fairePriceOps = new Set([
      OPERATIONS[FAIRE].PRICES.SET,
      OPERATIONS[FAIRE].PRICES.INCREASE_BY,
      OPERATIONS[FAIRE].PRICES.DECREASE_BY,
    ]);
    const originalById = bulkEdit.get('products').get('byId');

    previewOperations = previewOperations
      .map(operation => {
        if (!fairePriceOps.has(operation.get('type'))) return operation;

        let value = operation.get('value');
        for (const key of value.keys()) {
          const mapping = PRICE_API_KEY_TO_ATTRIBUTE_CURRENCY_MAP[key];
          if (!mapping) continue;
          const { attribute, currency } = mapping;
          const path = [FORM.INVENTORY, attribute, VALUE, currency];
          const allUnchanged = operation.get('products', EMPTY.LIST).every(productId =>
            originalById.getIn([productId, ...path]) === products.getIn(['byId', productId, ...path])
          );
          if (allUnchanged) {
            value = value.delete(key);
          }
        }

        return getSize(value) > 0 ? operation.set('value', value) : undefined;
      })
      .filter(isTruthy);

    operations = bulkEdit.get('operations', EMPTY.LIST).concat(previewOperations);
  }

  if (confirmation) {
    if (!bulkEdit.has('modal')) {
      yield function effect(dispatch) {
        dispatch(actions.bulkEdit.setModal({ action: actions.bulkEdit.applyPreview(), type: confirmation }));
      };

      return reduction;
    } else if (bulkEdit.getIn(['modal', 'type']) === confirmation) {
      bulkEdit = bulkEdit.delete('modal');
    }
  }

  bulkEdit = bulkEdit
    .set('operations', operations)
    .set('preview', preview)
    .set('products', products);

  if (bulkEdit.get('vela')) {
    yield function effect(dispatch) {
      dispatch(actions.bulkEdit.saveAs(SAVE_TYPE.SAVE));
    };
  } else {
    if (getSize(operationActions)) {
      yield function effect(dispatch) {
        operationActions.forEach(dispatch);
      };
    }

    bulkEdit = bulkEdit.setIn(['errors', 'counts'],
      setErrorCounts({ channel, counts: EMPTY.MAP, products: bulkEdit.get('products') })
    );
  }

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* bootstrap(reduction, { lastRoute }) {
  abortAll();
  let bulkEdit = reduction.get('bulkEdit');
  const channel = bulkEdit.get('channel');
  const shopId = bulkEdit.get('shopId');
  const db = bulkEdit.get('db');
  const products = bulkEdit.getIn(['products', 'all']);

  if (!shopId) {
    yield function effect() {
      goBack(lastRoute);
    };

    return reduction;
  }

  bulkEdit = bulkEdit
    .set('applyDisabled', true)
    .set('errors', Map({ products: EMPTY.SET, counts: EMPTY.MAP }))
    .set('filters', Map({ ids: products, limit: PAGE_SIZE, offset: 0 }));

  let menu = Map({
    expanded: Map({ [MENU_SECTION.MEDIA]: true }),
    items: MENU_ITEMS[channel].delete(MENU_ITEM.TAXONOMY),
    sections: Map({
      groups: MENU_SECTIONS[channel],
      items: MENU_SECTION_ITEMS[channel],
    }),
    selected: Map({ group: MENU_SECTION.MEDIA, item: MENU_ITEM.PHOTOS }),
  });

  function removeOperation(operation) {
    return function updateOperations(operations) {
      function filterMenuOperations(item) {
        return item !== operation;
      }

      return operations.filter(filterMenuOperations);
    };
  }

  function selectDefaultOperation(source) {
    const operations = source.getIn(['items', source.getIn(['selected', 'item']), 'operations']);

    if (getSize(operations) > 0) {
      const plan = reduction.getIn(['user', 'subscriptions', shopId, 'plan']);
      const defaultOperation = getDefaultOperation({ operations, plan });

      if (defaultOperation) {
        return source.set('operation', defaultOperation);
      }
    }

    return source;
  }

  switch (channel) {
    case EBAY: {
      if (getSize(reduction.getIn(['data', 'shopsData', shopId, 'storeCategories', 'options'])) > 0) {
        menu = menu.updateIn(['sections', 'items', MENU_SECTION.LISTINGS], function update(items) {
          return items.splice(3, 0, MENU_ITEM.STORE_CATEGORY);
        });
      }

      if (getSize(reduction.getIn(['data', 'shopsData', shopId, 'paymentPolicies', 'options'])) > 0) {
        menu = menu.updateIn(['sections', 'items', MENU_SECTION.INVENTORY], function update(items) {
          return items.splice(4, 0, MENU_ITEM.PAYMENT_POLICY);
        });
      }

      menu = selectDefaultOperation(menu.set('type', ATTRIBUTE.PHYSICAL));
      bulkEdit = bulkEdit.set('menu', menu).setIn(['products', ATTRIBUTE.PHYSICAL], products);

      yield function effect(dispatch) {
        const ids = products.toArray();
        const { signal } = categoriesController.start();

        api.listings.getCategoryIds({ channel, db, payload: { ids }, shopId, signal })
          .then(
            (data) => dispatch(actions.bulkEdit.bootstrap.fulfilled({ data: Set(data), menu })),
            (error) => dispatch(actions.bulkEdit.bootstrap.rejected({ error, lastRoute, signal })),
          );
      };

      break;
    }

    case ETSY: {
      if (!reduction.getIn(['user', 'features', FEATURE.BULK_EDIT_ALT_TEXT])) {
        menu = menu.updateIn(['items', MENU_ITEM.PHOTOS, 'operations'],
          removeOperation(OPERATIONS[ETSY].PHOTOS.ALT_TEXT)
        );
      }

      if (!reduction.getIn(['user', 'features', FEATURE.ETSY_PERSONALIZATION_QUESTIONS])) {
        menu = menu.setIn(['items', MENU_ITEM.PERSONALIZATION, 'operations'],
          List([
            OPERATIONS[ETSY].PERSONALIZATION.INSTRUCTIONS_ADD_BEFORE,
            OPERATIONS[ETSY].PERSONALIZATION.INSTRUCTIONS_ADD_AFTER,
            OPERATIONS[ETSY].PERSONALIZATION.INSTRUCTIONS_FIND_AND_REPLACE,
            OPERATIONS[ETSY].PERSONALIZATION.INSTRUCTIONS_DELETE,
            OPERATIONS[ETSY].PERSONALIZATION.INSTRUCTIONS_SET,
          ])
        );
      }

      menu = selectDefaultOperation(menu);

      yield function effect(dispatch) {
        const { signal } = categoriesController.start();
        const ids = products.toArray();
        const digital = FILTER[ETSY].IS_DIGITAL;

        Promise.all([
          api.listings.getListingIds({ channel, db, payload: { ids, [digital]: true }, shopId, signal }),
          api.listings.getCategoryIds({ channel, db, payload: { ids }, shopId, signal }),
        ])
          .then(
            (data) => dispatch(actions.bulkEdit.bootstrap.fulfilled({ data, menu })),
            (error) => dispatch(actions.bulkEdit.bootstrap.rejected({ error, lastRoute, signal })),
          );
      };

      break;
    }

    case FAIRE: {
      menu = selectDefaultOperation(menu.set('type', ATTRIBUTE.PHYSICAL));
      bulkEdit = bulkEdit.set('menu', menu).setIn(['products', ATTRIBUTE.PHYSICAL], products);
      break;
    }

    case SHOPIFY: {
      if (!reduction.getIn(['user', 'features', FEATURE.BULK_EDIT_ALT_TEXT])) {
        menu = menu.updateIn(['items', MENU_ITEM.PHOTOS, 'operations'],
          removeOperation(OPERATIONS[SHOPIFY].PHOTOS.ALT_TEXT)
        );
      }

      menu = selectDefaultOperation(menu.set('type', ATTRIBUTE.PHYSICAL));
      bulkEdit = bulkEdit.set('menu', menu).setIn(['products', ATTRIBUTE.PHYSICAL], products);
      break;
    }

    default: {
      menu = selectDefaultOperation(menu.set('type', ATTRIBUTE.PHYSICAL));
      bulkEdit = bulkEdit.set('menu', menu).setIn(['products', ATTRIBUTE.PHYSICAL], products);
      break;
    }
  }

  yield function effect(dispatch) {
    dispatch(actions.bulkEdit.getErrorCounts());
    dispatch(actions.bulkEdit.getProducts());

    if (bulkEdit.get('vela')) {
      dispatch(actions.bulkEdit.getInvalidProducts({ menu, products }));
    }
  };

  return reduction.set('bulkEdit', bulkEdit);
}

function* bootstrapFulfilled(reduction, { data, menu: source }) {
  if (!getSize(reduction.get('bulkEdit'))) return reduction;

  abort(categoriesController);

  let menu = source;
  let bulkEdit = reduction.get('bulkEdit');
  const channel = bulkEdit.get('channel');
  const shopId = bulkEdit.get('shopId');

  function updateListings(items) {
    return items.push(MENU_ITEM.FILES);
  }

  function updateInventory(items) {
    function filterItems(item) {
      switch (item) {
        case MENU_ITEM.VARIATIONS: {
          return false;
        }

        default: {
          return true;
        }
      }
    }

    return items.filter(filterItems);
  }

  function updateSectionIds(sections) {
    function filterSections(section) {
      switch (section) {
        case MENU_SECTION.SHIPPING: {
          return false;
        }

        default: {
          return true;
        }
      }
    }

    return sections.filter(filterSections);
  }

  function reduceIds(result, productId) {
    if (!result.digital.has(productId)) {
      result.physical = result.physical.push(productId);
    }

    return result;
  }

  switch (channel) {
    case EBAY: {
      const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
      const marketplaceId = reduction.getIn(['shops', 'byId', shopId, 'marketplaceId']);

      if (
        getSize(data) === 0 ||
        allCategoriesLoaded({
          categories: data,
          loaded: reduction.getIn(['data', 'loaded', 'taxonomy', channel, marketplaceId], EMPTY.SET),
        })
      ) {
        if (getSize(data) > 0) {
          menu = updateMenuTaxonomyAttrubutes({ categories: data, channel, marketplaceId, menu, userFeatures });
          menu = updateMenuConditions({ categories: data, channel, marketplaceId, menu });
        } else {
          menu = menu.deleteIn(['sections', 'items', MENU_SECTION.TAXONOMY]);
          menu = removeConditionFromMenu(menu);
        }
      } else {
        menu = menu.set('categories', data);

        yield function effect(dispatch) {
          data.forEach(function forEachCategory(category) {
            dispatch(actions.data.getTaxonomyData({ category, channel, marketplaceId }));
          });
        };
      }

      break;
    }

    case ETSY: {
      if (getSize(data[0]) !== getSize(reduction.getIn(['bulkEdit', 'products', 'all']))) {
        if (getSize(data[0])) {
          const digital = data[0].map(shapeId);
          menu = menu.updateIn(['sections', 'items', MENU_SECTION.LISTINGS], updateListings);

          bulkEdit = bulkEdit
            .setIn(['products', ATTRIBUTE.DIGITAL], List(digital))
            .setIn(['products', ATTRIBUTE.PHYSICAL],
              bulkEdit
                .getIn(['products', 'all'])
                .reduce(reduceIds, { digital: Set(digital), physical: EMPTY.LIST }).physical
            );
        } else {
          menu = menu.set('type', ATTRIBUTE.PHYSICAL);
          bulkEdit = bulkEdit.setIn(['products', ATTRIBUTE.PHYSICAL], bulkEdit.getIn(['products', 'all']));
        }
      } else {
        menu = menu
          .set('type', ATTRIBUTE.DIGITAL)
          .updateIn(['sections', 'groups'], updateSectionIds)
          .updateIn(['sections', 'items', MENU_SECTION.INVENTORY], updateInventory)
          .updateIn(['sections', 'items', MENU_SECTION.LISTINGS], updateListings);

        bulkEdit = bulkEdit.setIn(['products', ATTRIBUTE.DIGITAL], bulkEdit.getIn(['products', 'all']));
      }

      if (getSize(data[1])) {
        const categories = Set(data[1]);
        const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
        menu = updateMenuTaxonomyAttrubutes({ categories, channel, menu, userFeatures });
      } else {
        menu = menu.deleteIn(['sections', 'items', MENU_SECTION.TAXONOMY]);
      }

      break;
    }

    default: {
      break;
    }
  }

  return reduction.set('bulkEdit', bulkEdit.set('menu', menu));
}

function* bootstrapRejected(reduction, { error, lastRoute, signal }) {
  if (signal.aborted || !getSize(reduction.get('bulkEdit'))) return reduction;

  abort(categoriesController);

  yield function effect() {
    console.error(error);
    goBack(lastRoute);
  };

  return reduction;
}

function* closeBulkEdit(reduction, { route, repercussion = false } = {}) {
  abortAll();

  if (repercussion) {
    return reduction.set('bulkEdit', EMPTY.MAP);
  }

  if (
    getSize(reduction.getIn(['bulkEdit', 'operations'])) || (
      reduction.getIn(['bulkEdit', 'vela']) &&
      getSize(reduction.getIn(['bulkEdit', 'preview']))
    )
  ) {
    yield function effect(dispatch) {
      dispatch(
        actions.bulkEdit.setModal({
          type: MODALS.CONFIRMATIONS.CANCEL,
          route: route || getListingsPath(reduction),
        })
      );
    };

    return reduction;
  }

  yield function effect() {
    navigateTo(route || getListingsPath(reduction));
  };

  return reduction.set('bulkEdit', EMPTY.MAP);
}

function* getCategories(reduction) {
  yield function effect(dispatch) {
    const { signal } = categoriesController.start();
    const bulkEdit = reduction.get('bulkEdit');
    const channel = bulkEdit.get('channel');
    const db = bulkEdit.get('db');
    const shopId = bulkEdit.get('shopId');
    const ids = bulkEdit.getIn(['products', 'all']).toArray();

    api.listings.getCategoryIds({ channel, db, payload: { ids }, shopId, signal })
      .then(
        (response) => dispatch(actions.bulkEdit.getCategories.fulfilled(Set(response))),
        (error) => dispatch(actions.bulkEdit.getCategories.rejected({ error, signal })),
      );

  };

  return reduction;
}

function* getCategoriesFulfilled(reduction, categories) {
  if (!getSize(reduction.get('bulkEdit'))) return reduction;

  abort(categoriesController);

  let bulkEdit = reduction.get('bulkEdit');
  const channel = bulkEdit.get('channel');
  const shopId = bulkEdit.get('shopId');
  const userFeatures = reduction.getIn(['user', 'features']);
  const shopData = reduction.getIn(['data', 'shopsData', shopId], EMPTY.MAP);
  const shop = reduction.getIn(['shops', 'byId', shopId]);
  const marketplaceId = shop.get('marketplaceId');
  let menu = bulkEdit.get('menu');

  function revalidateProduct(product) {
    return setProductErrors({ product, shop, shopData, userFeatures });
  }

  if (
    channel === EBAY &&
    !allCategoriesLoaded({
      categories,
      loaded: reduction.getIn(['data', 'loaded', 'taxonomy', channel, marketplaceId], EMPTY.SET),
    })
  ) {
    yield function effect(dispatch) {
      categories.forEach(function forEachCategory(category) {
        dispatch(actions.data.getTaxonomyData({ category, channel, marketplaceId }));
      });
    };

    return reduction.setIn(['bulkEdit', 'menu'], menu.set('categories', categories));
  } else if (menu.has('categories')) {
    menu = menu.delete('categories');

    if (getSize(bulkEdit.getIn(['products', 'byId'])) > 0) {
      bulkEdit = bulkEdit.setIn(['products', 'byId'], bulkEdit.getIn(['products', 'byId']).map(revalidateProduct));

      if (!bulkEdit.get('vela')) {
        bulkEdit = bulkEdit.setIn(['errors', 'counts'],
          setErrorCounts({ channel, counts: EMPTY.MAP, products: bulkEdit.get('products') })
        );
      }
    }
  }

  menu = clearMenuTaxonomyAttrubutes(menu);

  if (getSize(categories)) {
    menu = updateMenuTaxonomyAttrubutes({ categories, channel, marketplaceId, menu, userFeatures });

    if (menu.getIn(['selected', 'group']) === MENU_SECTION.TAXONOMY) {
      const selectedMenuItem = menu.getIn(['selected', 'item']);

      if (
        !menu.hasIn(['items', selectedMenuItem]) ||
        !menu.getIn(['items', selectedMenuItem, 'filters'], EMPTY.MAP).equals(
          bulkEdit.getIn(['menu', 'items', selectedMenuItem, 'filters'], EMPTY.MAP)
        )
      ) {
        yield function effect(dispatch) {
          const item = menu.getIn(['sections', 'items', MENU_SECTION.TAXONOMY, 0]);
          dispatch(actions.bulkEdit.selectMenuItem({ group: MENU_SECTION.TAXONOMY, item }));
        };
      }
    }

    if (channel === EBAY) {
      menu = updateMenuConditions({ categories, channel, marketplaceId, menu });
    }
  } else {
    menu = menu.deleteIn(['sections', 'items', MENU_SECTION.TAXONOMY]);

    if (menu.getIn(['selected', 'group']) === MENU_SECTION.TAXONOMY) {
      yield function effect(dispatch) {
        dispatch(actions.bulkEdit.selectMenuItem({ group: MENU_SECTION.MEDIA, item: MENU_ITEM.PHOTOS }));
      };
    }

    if (channel === EBAY) {
      menu = removeConditionFromMenu(menu);
    }
  }

  return reduction.set('bulkEdit', bulkEdit.set('menu', menu));
}

function* getCategoriesRejected(reduction, { error, signal }) {
  if (signal.aborted || !getSize(reduction.get('bulkEdit'))) return reduction;

  abort(categoriesController);

  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction;
}

function* getErrorCounts(reduction) {
  if (!reduction.getIn(['bulkEdit', 'vela'])) return reduction;

  const bulkEdit = reduction.get('bulkEdit');
  const db = bulkEdit.get('db');
  const channel = bulkEdit.get('channel');
  const shopId = bulkEdit.get('shopId');
  const id = bulkEdit.getIn(['products', 'all']).toArray();

  yield function effect(dispatch) {
    api
      .products
      .getInvalidCounts({ channel, db, id, shopId })
      .then(
        (data) => dispatch(actions.bulkEdit.getErrorCounts.fulfilled(data)),
        (error) => dispatch(actions.bulkEdit.getErrorCounts.rejected(error))
      );
  };

  return reduction.set('bulkEdit', bulkEdit);
}

function* getErrorCountsFulfilled(reduction, data) {
  if (!getSize(reduction.get('bulkEdit'))) return reduction;

  const channel = reduction.getIn(['bulkEdit', 'channel']);

  return reduction.setIn(['bulkEdit', 'errors', 'counts'],
    shapeErrorCountsForApp({ channel, data })
  );
}

function* getErrorCountsRejected(reduction, error) {
  if (!getSize(reduction.get('bulkEdit'))) return reduction;

  console.error(error);

  return reduction;
}

function* getFilterIds(reduction, selection = SELECTION.ALL) {
  abort(listingIdsController);

  const bulkEdit = reduction.get('bulkEdit').set('processing', true);
  const userFeatures = reduction.getIn(['user', 'features']);

  yield function effect(dispatch) {
    const { signal } = listingIdsController.start();
    const channel = bulkEdit.get('channel');
    const db = bulkEdit.get('db');
    const shopId = bulkEdit.get('shopId');
    const payload = shapeFiltersForAPI({
      channel,
      errors: bulkEdit.get('errors'),
      filters: bulkEdit.get('filters'),
      menu: bulkEdit.get('menu'),
      products: bulkEdit.get('products'),
      selection: bulkEdit.get('selection'),
    });

    const { ids, ...rest } = payload;

    if (getSize(rest) === 0) {
      dispatch(actions.bulkEdit.getFilterIds.fulfilled({ response: ids, selection }));
      return;
    }

    api.listings.getListingIds({ channel, db, payload, shopId, signal })
      .then(
        (response) => {
          if (Array.isArray(response)) {
            dispatch(actions.bulkEdit.getFilterIds.fulfilled({ response, selection }));
          } else {
            dispatch(actions.bulkEdit.getFilterIds.rejected({ error: `Response is not an array: ${response}`, signal }));
          }
        },
        (error) => dispatch(actions.bulkEdit.getFilterIds.rejected({ error, signal })),
      );
  };

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* getFilterIdsFulfilled(reduction, { response, selection }) {
  if (getSize(reduction.get('bulkEdit')) === 0) return reduction;

  abort(listingIdsController);

  const products = response.map(shapeId);

  yield function effect(dispatch) {
    if (getSize(products) > 0) {
      dispatch(actions.bulkEdit.getProducts({ selection }));
    } else {
      dispatch(
        actions.bulkEdit.getProducts.fulfilled({
          data: { count: 0 },
          selection: SELECTION.NONE,
        })
      );
    }
  };

  return reduction.setIn(['bulkEdit', 'filters', 'ids'], List(products));
}

function* getFilterIdsRejected(reduction, { error, signal }) {
  if (signal.aborted || getSize(reduction.get('bulkEdit')) === 0) return reduction;

  abort(listingIdsController);

  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_LISTINGS,
      })
    );
  };

  return reduction.deleteIn(['bulkEdit', 'processing']);
}

function* getInvalidProducts(reduction, { menu, products } = {}) {
  if (!reduction.getIn(['bulkEdit', 'vela'])) return reduction;

  abort(invalidController);

  const bulkEdit = reduction.get('bulkEdit');
  const db = bulkEdit.get('db');
  const channel = bulkEdit.get('channel');
  const shopId = bulkEdit.get('shopId');
  const id = (products || bulkEdit.getIn(['products', 'all'])).toArray();
  const menuItem = (menu || bulkEdit.get('menu')).getIn(['selected', 'item']);
  const attribute = MENU_ITEMS_TO_ATTRIBUTE_MAP[channel][menuItem];
  const userFeatures = reduction.getIn(['user', 'features']);

  yield function effect(dispatch) {
    if (!attribute) {
      dispatch(actions.bulkEdit.getInvalidProducts.fulfilled());
      return;
    }

    const { signal } = invalidController.start();

    api.products.getInvalid({ attribute, channel, db, id, shopId, signal })
      .then(
        (response) => dispatch(actions.bulkEdit.getInvalidProducts.fulfilled(response)),
        (error) => dispatch(actions.bulkEdit.getInvalidProducts.rejected({ error, signal }))
      );
  };

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* getInvalidProductsFulfilled(reduction, response = {}) {
  if (!reduction.getIn(['bulkEdit', 'vela'])) return reduction;

  abort(invalidController);

  let data = reduction.get('bulkEdit');
  const products = List((response.products_ids || []).map(shapeId));

  if (data.get('selection') === SELECTION.INCOMPLETE) {
    if (getSize(products) === 0) {
      data = data.setIn(['errors', 'products'], EMPTY.LIST);

      yield function effect(dispatch) {
        dispatch(actions.bulkEdit.setSelection({ selection: SELECTION.ALL }));
      };
    } else {
      data = data
        .setIn(['errors', 'products'], products)
        .setIn(['filters', 'ids'], products);

      yield function effect(dispatch) {
        dispatch(actions.bulkEdit.getProducts({ selection: SELECTION.INCOMPLETE }));
      };
    }
  } else {
    data = data.setIn(['errors', 'products'], getSize(products) === 0 ? EMPTY.LIST : products);
  }

  return reduction.set('bulkEdit', data);
}

function* getInvalidProductsRejected(reduction, { error, signal }) {
  if (signal.aborted || !reduction.getIn(['bulkEdit', 'vela'])) return reduction;

  abort(invalidController);
  console.error(error);

  return reduction;
}

function* getProducts(reduction, { selection } = {}) {
  if (!reduction.getIn(['bulkEdit', 'shopId'])) return reduction;

  abort(listingsController);
  const userFeatures = reduction.getIn(['user', 'features']);
  const bulkEdit = reduction.get('bulkEdit').set('processing', true);
  const channel = bulkEdit.get('channel');
  const db = bulkEdit.get('db');
  const shopId = bulkEdit.get('shopId');
  const filters = bulkEdit.get('filters');
  const limit = filters.get('limit');
  const offset = filters.get('offset');
  const ids = filters.get('ids');
  const payload = {
    id: ids.slice(offset, offset + limit).toArray(),
  };

  yield function effect(dispatch) {
    const { signal } = listingsController.start();
    api.products.getById({ channel, db, payload, shopId, signal })
      .then(
        (response) => dispatch(actions.bulkEdit.getProducts.fulfilled({ payload, response, selection, signal })),
        (error) => dispatch(actions.bulkEdit.getProducts.rejected({ error, signal }))
      );
  };

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* getProductsFulfilled(reduction, { payload, response, selection, signal }) {
  if (!getSize(reduction.get('bulkEdit'))) return reduction;

  let bulkEdit = reduction.get('bulkEdit').deleteAll(['processing', 'saving']);
  const filters = bulkEdit.get('filters');
  const channel = bulkEdit.get('channel');
  const shopId = bulkEdit.get('shopId');
  const userFeatures = reduction.getIn(['user', 'features']);
  const shop = reduction.getIn(['shops', 'byId', shopId]);
  const shopData = reduction.getIn(['data', 'shopsData', shopId], EMPTY.MAP);

  if (!response?.count) {
    bulkEdit = bulkEdit
      .set('page', getPageInfo(filters, 0))
      .setIn(['products', 'byId'], EMPTY.MAP)
      .setIn(['products', 'ids'], EMPTY.LIST)
      .setIn(['products', 'total'], 0);

    if (selection) {
      bulkEdit = bulkEdit.setIn(['products', 'selected'], EMPTY.ORDERED_SET);
    }

    return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
  } else if (bulkEdit.getIn(['products', 'total']) !== getSize(bulkEdit.getIn(['filters', 'ids']))) {
    bulkEdit = bulkEdit.setIn(['products', 'total'], getSize(bulkEdit.getIn(['filters', 'ids'])));
  }

  function reduceProductIds(result, id) {
    const productId = shapeId(id);
    const product = shapeProductForApp({ data: response, productId, shop, shopData, userFeatures });

    function updateById(byId = EMPTY.MAP) {
      return byId.set(productId, product);
    }

    function updateIds(ids = EMPTY.LIST) {
      return ids.push(productId);
    }

    return result
      .update('byId', updateById)
      .update('ids', updateIds);
  }

  const operations = bulkEdit.get('operations');
  let products;

  try {
    products = payload.id.reduce(reduceProductIds, bulkEdit.get('products').delete('byId').delete('ids'));
  } catch (error) {
    yield function effect(dispatch) {
      dispatch(actions.bulkEdit.getProducts.rejected({ error, signal }));
    };

    return reduction;
  }

  abort(listingsController);

  if (selection) {
    switch (selection) {
      case SELECTION.ALL: {
        products = products.set('selected', filters.get('ids').toOrderedSet());
        break;
      }

      case SELECTION.INCOMPLETE: {
        products = products.set('selected', bulkEdit.getIn(['errors', 'products']).toOrderedSet());
        break;
      }

      case SELECTION.NONE: {
        products = products.set('selected', EMPTY.ORDERED_SET);
        break;
      }

      case SELECTION.PAGE: {
        products = products.set('selected', products.get('ids').toOrderedSet());
        break;
      }

      default: {
        break;
      }
    }
  }

  const updates = applyOperationsToProducts({
    operations,
    products,
    shop,
    shopData,
    userFeatures,
  });

  bulkEdit = bulkEdit
    .set('page', getPageInfo(filters, bulkEdit.getIn(['products', 'total'])))
    .set('products', updates.products);

  if (!bulkEdit.get('vela')) {
    bulkEdit = bulkEdit.setIn(['errors', 'counts'],
      setErrorCounts({ channel, counts: EMPTY.MAP, products: updates.products })
    );
  }

  yield function effect(dispatch) {
    const preview = bulkEdit.get('preview', EMPTY.MAP);
    const previewActions = getPreviewActions({ channel, preview });

    if (getSize(previewActions) > 0) {
      previewActions.forEach(dispatch);
    }
  };

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* getProductsRejected(reduction, { error, signal }) {
  if (signal.aborted || !getSize(reduction.get('bulkEdit'))) return reduction;

  abort(listingsController);

  yield function effect(dispatch) {
    console.error('Error encountered while getting products: ', error);

    if (!reduction.hasIn(['bulkEdit', 'filters'])) {
      dispatch(actions.bulkEdit.closeBulkEdit());
    }

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  const bulkEdit = reduction.get('bulkEdit').delete('processing');
  const userFeatures = reduction.getIn(['user', 'features']);

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* getProfileListings(state, profileId) {
  abort(profileListingsController);

  if (!profileId || profileId !== state.getIn(['bulkEdit', 'profile', 'id'])) return state;

  const profile = state.getIn(['bulkEdit', 'profile']);
  const listings = profile.get('listings');
  const page = listings.get('page', -1) + 1;
  const limit = PAGE_SIZE;
  const offset = page * limit;

  if (offset >= listings.get('total')) return state;

  yield function effect(dispatch) {
    const { signal } = profileListingsController.start();

    api.profiles.getListings({ limit, offset, profileId, signal, type: profile.get('type') })
      .then(function onFulfilled(response) {
        dispatch(actions.bulkEdit.getProfileListings.fulfilled({ page, profileId, response }));
      })
      .catch(function onRejected(error) {
        dispatch(actions.bulkEdit.getProfileListings.rejected({ error, profileId, signal }));
      });
  };

  return state.setIn(['bulkEdit', 'profile', 'listings', 'loading'], true);
}

function* getProfileListingsFulfilled(state, { page, profileId, response }) {
  if (profileId !== state.getIn(['bulkEdit', 'profile', 'id'])) {
    return state;
  }

  abort(profileListingsController);

  function updateListings(listings) {
    return shapeListingsForApp({ response, state: listings.delete('loading').set('page', page) });
  }

  return state.updateIn(['bulkEdit', 'profile', 'listings'], updateListings);
}

function* getProfileListingsRejected(state, { error, profileId, signal }) {
  if (signal.aborted || profileId !== state.getIn(['bulkEdit', 'profile', 'id'])) {
    return state;
  }

  abort(profileListingsController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_LISTINGS,
      })
    );
  };

  return state.deleteIn(['bulkEdit', 'profile', 'listings', 'loading']);
}

function* removePreview(reduction, attributes) {
  if (!getSize(reduction.getIn(['bulkEdit', 'preview']))) return reduction;

  let bulkEdit = reduction.get('bulkEdit');
  const preview = bulkEdit.get('preview', EMPTY.MAP);
  const userFeatures = reduction.getIn(['user', 'features']);

  function reduceAttributes(result, key) {
    if (Array.isArray(key)) {
      return result.deleteIn(key);
    } else if (typeof key === 'string') {
      return result.delete(key);
    } else {
      return result;
    }
  }

  switch (typeof attributes) {
    case 'string': {
      bulkEdit = bulkEdit.deleteIn(['preview', attributes]);
      break;
    }

    case 'object': {
      if (Array.isArray(attributes) || List.isList(attributes)) {
        bulkEdit = bulkEdit.set('preview', attributes.reduce(reduceAttributes, preview));
      } else {
        bulkEdit = bulkEdit.set('preview', EMPTY.MAP);
      }

      break;
    }

    default: {
      bulkEdit = bulkEdit.set('preview', EMPTY.MAP);
      break;
    }
  }

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* replaceOperation(reduction, operation) {
  function updateEdited(edited = EMPTY.SET) {
    return edited.union(operation.get('products'));
  }

  function updateOperations(operations = EMPTY.LIST) {
    function filterSameOperation(item) {
      return (
        item.get('type') !== operation.get('type') ||
        !areEqual(item.get('products'), operation.get('products'))
      );
    }

    return operations.filter(filterSameOperation).push(operation);
  }

  return reduction
    .updateIn(['bulkEdit', 'operations'], updateOperations)
    .updateIn(['bulkEdit', 'products', 'edited'], updateEdited);
}

function* saveAs(reduction, saveType) {
  switch (saveType) {
    case SAVE_TYPE.SAVE: {
      const userFeatures = reduction.getIn(['user', 'features']);
      let bulkEdit = reduction
        .get('bulkEdit')
        .delete('modal')
        .set('saving', true);

      const shopId = bulkEdit.get('shopId');
      const channel = bulkEdit.get('channel');
      const route = bulkEdit.getIn(['modal', 'route']);
      const products = bulkEdit.getIn(['products', 'edited']).toList();

      if (bulkEdit.get('status') === STATUS[VELA].COPIED) {
        // this operation is required to Save copied listings withing Vela db
        // and do not trigger sync process to the channel
        const operation = Map({ products, type: OPERATIONS[channel].STATUS, value: STATUS[VELA].COPIED });
        bulkEdit = bulkEdit.set('operations', bulkEdit.get('operations', EMPTY.LIST).push(operation));
      }

      yield function effect(dispatch) {
        dispatch(actions.bulkEdit.syncUpdates({ route }));

        dispatch(
          actions.shops.setData({
            path: ['syncData', shopId],
            value: Map({
              indicator: SYNC_INDICATOR.SAVE,
            }),
          })
        );

        dispatch(
          actions.listings.toggleState({
            listings: products,
            state: ['processing', shopId],
          })
        );
      };

      return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
    }

    case SAVE_TYPE.SYNC: {
      const userFeatures = reduction.getIn(['user', 'features']);
      const bulkEdit = reduction.get('bulkEdit').delete('modal').set('saving', true);

      yield function effect(dispatch) {
        const shopId = bulkEdit.get('shopId');
        const route = reduction.getIn(['bulkEdit', 'modal', 'route']);

        dispatch(actions.bulkEdit.syncUpdates({ route }));
        dispatch(
          actions.shops.setData({
            path: ['syncData', shopId],
            value: Map({
              indicator: SYNC_INDICATOR.SYNC,
            }),
          })
        );
      };

      return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
    }

    default: {
      return reduction;
    }
  }
}

function* scheduleUpdates(reduction, { data, startDate }) {
  if (reduction.hasIn(['bulkEdit', 'menu', 'profileUpdates'])) return reduction;

  if (!data) {
    const userFeatures = reduction.getIn(['user', 'features']);
    const empty = reduction.get('bulkEdit')
      .set('processing', true)
      .set('syncStarted', true)
      .delete('operations')
      .deleteIn(['menu', 'pendingUpdates'])
      .deleteIn(['menu', 'profileUpdates']);

    yield function effect(dispatch) {
      dispatch(actions.bulkEdit.scheduleUpdates({ data: reduction.get('bulkEdit'), startDate }));
    };

    return reduction.set('bulkEdit', empty.set('applyDisabled', isApplyDisabled({ bulkEdit: empty, userFeatures })));
  }

  yield function effect(dispatch) {
    const channel = data.get('channel');
    const db = data.get('db');
    const shopId = data.get('shopId');
    const operations = data.get('operations').toJS();

    api.schedule.updates
      .create({ channel, db, operations, shopId, startDate, type: 'updates' })
      .then(
        () => dispatch(actions.bulkEdit.scheduleUpdates.fulfilled(shopId)),
        (error) => dispatch(actions.bulkEdit.scheduleUpdates.rejected({ data, error })),
      );
  };

  return reduction.deleteIn(['bulkEdit', 'syncStarted']);
}

function* scheduleUpdatesFulfilled(reduction, shopId) {
  yield function effect(dispatch) {
    dispatch(actions.shops.getScheduled(shopId));
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.SCHEDULE_UPDATES,
      })
    );
  };

  let bulkEdit = reduction.get('bulkEdit');

  if (!getSize(bulkEdit)) return reduction;

  bulkEdit = bulkEdit.delete('processing');
  const userFeatures = reduction.getIn(['user', 'features']);

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* scheduleUpdatesRejected(reduction, { data, error }) {
  yield function effect(dispatch) {
    console.error('Error encountered while scheduling updates: ', error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.SCHEDULE_UPDATES,
      })
    );
  };

  return reduction.getIn(['bulkEdit', 'shopId']) === data.get('shopId')
    ? reduction.set('bulkEdit', data)
    : reduction;
}

function* search(reduction, query) {
  if (!getSize(reduction.get('bulkEdit'))) return reduction;

  let bulkEdit = reduction.get('bulkEdit');
  let filters = bulkEdit.get('filters');
  const channel = bulkEdit.get('channel');
  const userFeatures = reduction.getIn(['user', 'features']);

  if ((!query && !filters.get(FILTER[channel].TITLE)) || query === filters.get(FILTER[channel].TITLE)) return reduction;

  abort(listingsController);
  abort(listingIdsController);

  if (query) {
    filters = filters.set(FILTER[channel].TITLE, query);
  } else {
    filters = filters.delete(FILTER[channel].TITLE);
  }

  bulkEdit = bulkEdit.set('filters', filters.delete('ids').set('offset', 0)).set('processing', true);

  yield function effect(dispatch) {
    dispatch(actions.bulkEdit.getFilterIds(SELECTION.ALL));
  };

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* selectOperation(reduction, { operation, preview }) {
  let bulkEdit = reduction
    .get('bulkEdit')
    .setIn(['menu', 'operation'], operation);

  const userFeatures = reduction.getIn(['user', 'features']);

  if (getSize(preview) > 0) {
    bulkEdit = bulkEdit.set('preview', preview);
  } else {
    bulkEdit = bulkEdit.delete('preview');
  }

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* selectMenuItem(reduction, { group, item, operation }) {
  let bulkEdit = reduction.get('bulkEdit');
  const channel = bulkEdit.get('channel');
  const menu = bulkEdit.get('menu');
  const filters = bulkEdit.get('filters');
  const products = bulkEdit.get('products');
  const updatedFilters = updateFiltersForMenuItem({ channel, filters, item, menu, products });
  const userFeatures = reduction.getIn(['user', 'features']);

  if (updatedFilters) {
    abort(invalidController);
    abort(listingsController);
    abort(listingIdsController);

    yield function effect(dispatch) {
      if (bulkEdit.get('vela')) {
        dispatch(actions.bulkEdit.getInvalidProducts());

        if (bulkEdit.get('selection') !== SELECTION.INCOMPLETE) {
          dispatch(actions.bulkEdit.getFilterIds(SELECTION.ALL));
        }
      } else if (filters.get(FILTER[channel].TITLE) || !updatedFilters.has('ids')) {
        dispatch(actions.bulkEdit.getFilterIds(SELECTION.ALL));
      } else {
        dispatch(actions.bulkEdit.getProducts({ selection: SELECTION.ALL }));
      }
    };

    bulkEdit = bulkEdit
      .set('processing', true)
      .set('filters', updatedFilters)
      .deleteIn(['products', 'ids'])
      .deleteIn(['products', 'byId']);
  } else if (bulkEdit.get('vela')) {
    abort(invalidController);

    yield function effect(dispatch) {
      dispatch(actions.bulkEdit.getInvalidProducts());
    };

    if (bulkEdit.get('selection') === SELECTION.INCOMPLETE) {
      abort(listingsController);
      abort(listingIdsController);

      bulkEdit = bulkEdit
        .set('processing', true)
        .deleteIn(['products', 'ids'])
        .deleteIn(['products', 'byId']);
    }
  } else if (menu.getIn(['profileUpdates', item])) {
    const operations = bulkEdit.get('operations', EMPTY.LIST).filter(filterOperations(OPERATIONS[channel].AI));

    if (getSize(operations) > 0) {
      const shopId = bulkEdit.get('shopId');
      const shop = reduction.getIn(['shops', 'byId', shopId]);
      const shopData = reduction.getIn(['data', 'shopsData', shopId], EMPTY.MAP);
      const updates = applyOperationsToProducts({
        operations,
        products,
        shop,
        shopData,
        userFeatures,
      });

      if (getSize(updates.actions)) {
        yield function effect(dispatch) {
          updates.actions.forEach(dispatch);
        };
      } else if (bulkEdit.get('processing')) {
        bulkEdit = bulkEdit.delete('processing');
      }
    } else if (bulkEdit.get('processing')) {
      bulkEdit = bulkEdit.delete('processing');
    }
  } else if (bulkEdit.get('processing')) {
    bulkEdit = bulkEdit.delete('processing');
  }

  bulkEdit = bulkEdit.delete('preview').setIn(['menu', 'selected'], Map({ group, item }));

  const itemOperations = bulkEdit.getIn(['menu', 'items', item, 'operations']);

  if (getSize(itemOperations) > 0) {
    if (operation && itemOperations.includes(operation)) {
      bulkEdit = bulkEdit.setIn(['menu', 'operation'], operation);
    } else {
      const shopId = bulkEdit.get('shopId');
      const plan = reduction.getIn(['user', 'subscriptions', shopId, 'plan']);
      const defaultOperation = getDefaultOperation({ operations: itemOperations, plan });
      if (defaultOperation) {
        bulkEdit = bulkEdit.setIn(['menu', 'operation'], defaultOperation);
      }
    }
  } else {
    bulkEdit = bulkEdit.deleteIn(['menu', 'operation']);
  }

  if (!bulkEdit.getIn(['menu', 'expanded', group])) {
    if (getSize(bulkEdit.getIn(['menu', 'expanded'])) === 1) {
      bulkEdit = bulkEdit.setIn(['menu', 'expanded'], Map({ [group]: true }));
    } else {
      bulkEdit = bulkEdit.setIn(['menu', 'expanded', group], true);
    }
  }

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* setBanner(reduction, payload) {
  return payload
    ? reduction.setIn(['bulkEdit', 'banner'], Map(payload))
    : reduction.deleteIn(['bulkEdit', 'banner']);
}

function* setData(reduction, data) {
  function updateBulkEdit(bulkEdit = EMPTY.MAP) {
    let result = bulkEdit;

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        if (data[key] === undefined) {
          result = result.delete(key);
        } else {
          result = result.set(key, data[key]);
        }
      }
    }

    return result;
  }

  return reduction.update('bulkEdit', updateBulkEdit);
}

function* setModal(reduction, payload) {
  return payload
    ? reduction.setIn(['bulkEdit', 'modal'], Map(payload))
    : reduction.deleteIn(['bulkEdit', 'modal']);
}

function* setPage(reduction, page) {
  yield function effect(dispatch) {
    dispatch(actions.bulkEdit.getProducts());
  };

  return reduction
    .setIn(['bulkEdit', 'page', 'currentPage'], page)
    .setIn(['bulkEdit', 'filters', 'offset'], page * PAGE_SIZE);
}

function* setProduct(reduction, newProduct) {
  let state = reduction;
  const path = ['bulkEdit', 'products', 'byId', newProduct.get('productId')];
  const oldProduct = state.getIn(path);

  if (!oldProduct) return state;

  if (!state.getIn(['bulkEdit', 'vela'])) {
    if (!oldProduct.get(ERROR).equals(newProduct.get(ERROR))) {
      const channel = state.getIn(['bulkEdit', 'channel']);
      state = state.updateIn(['bulkEdit', 'errors', 'counts'],
        updateErrorCounts({ channel, oldProduct, newProduct })
      );
    }
  }

  return state.setIn(path, newProduct);
}

function* setProfile(state, profile = EMPTY.MAP) {
  let bulkEdit = state.get('bulkEdit');
  const userFeatures = state.getIn(['user', 'features']);

  abort(profileController);

  switch (profile.get('type')) {
    case PROFILE_TYPE.AI: {
      yield function effect(dispatch) {
        const profileId = profile.get('id');
        const { signal } = profileController.start();

        api.profiles.getById({ profileId, signal, type: PROFILE_TYPE.AI })
          .then(
            (response) => dispatch(actions.bulkEdit.setProfile.fulfilled({ profile, response })),
            (error) => dispatch(actions.bulkEdit.setProfile.rejected({ error, signal }))
          );
      };

      bulkEdit = bulkEdit.set('processing', true).set('profile', profile);
      break;
    }

    case PROFILE_TYPE.STANDARD: {
      yield function effect(dispatch) {
        const db = bulkEdit.get('db');
        const shopId = bulkEdit.get('shopId');
        const channel = bulkEdit.get('channel');
        const templateId = profile.get('templateId');
        const { signal } = profileController.start();

        api.products.getById({ channel, db, payload: { id: [templateId] }, shopId, signal })
          .then(
            (response) => dispatch(actions.bulkEdit.setProfile.fulfilled({ profile, response })),
            (error) => dispatch(actions.bulkEdit.setProfile.rejected({ error, signal }))
          );
      };

      bulkEdit = bulkEdit.set('processing', true).set('profile', profile);
      break;
    }

    default: {
      bulkEdit = bulkEdit.deleteAll(['processing', 'profile']);
      break;
    }
  }

  return state.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* setProfileFulfilled(state, { profile, response }) {
  if (getSize(state.get('bulkEdit')) === 0) return state;

  abort(profileController);

  let bulkEdit = state.get('bulkEdit');
  const channel = bulkEdit.get('channel');
  const shopId = bulkEdit.get('shopId');
  const selected = bulkEdit.getIn(['products', 'selected']);
  const edited = selected.toList();
  const userFeatures = state.getIn(['user', 'features'], EMPTY.MAP);
  const shopData = state.getIn(['data', 'shopsData', shopId], EMPTY.MAP);
  const shop = state.getIn(['shops', 'byId', shopId]);
  const marketplaceId = shop.get('marketplaceId');

  function updateEdited(oldEdited = EMPTY.SET) {
    return oldEdited.union(selected);
  }

  function mapOperations(operation) {
    return operation.set('products', edited);
  }

  switch (profile.get('type')) {
    case PROFILE_TYPE.AI: {
      let updates;
      const ai = shapeAiOperationsForApp(response.data);
      const operations = getAIProfileOperations({ channel, products: edited, profile: profile.set(AI, ai) });

      if (getSize(operations) === 0) {
        bulkEdit = bulkEdit.delete('processing');
        return state.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
      }

      if (state.hasIn(['bulkEdit', 'products', 'byId'])) {
        updates = applyOperationsToProducts({
          operations,
          products: bulkEdit.get('products').update('edited', updateEdited),
          shop,
          shopData,
          userFeatures,
        });
      }

      if (bulkEdit.get('vela')) {
        yield function effect(dispatch) {
          dispatch(actions.bulkEdit.saveAs(SAVE_TYPE.SAVE));
        };
      } else if (updates && getSize(updates.actions) > 0) {
        yield function effect(dispatch) {
          updates.actions.forEach(dispatch);
        };
      }

      bulkEdit = bulkEdit
        .update('profilesToConnect', function updateProfilesToConnect(profilesToConnect = EMPTY.LIST) {
          return profilesToConnect.push(
            Map({ id: profile.get('id'), listings: selected.toList(), type: profile.get('type') })
          );
        })
        .updateIn(['menu', 'profileUpdates'], function updateProfileUpdates(profileUpdates = EMPTY.SET) {
          return profileUpdates.union(getUpdatedMenuItemsFromOperations({ channel, operations }));
        })
        .set('operations', bulkEdit.get('operations', EMPTY.LIST).concat(operations))
        .set('preview', EMPTY.MAP);

      if (updates) {
        bulkEdit = bulkEdit.set('products', updates.products);
      }

      return state.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
    }

    case PROFILE_TYPE.STANDARD: {
      let updates;
      const templateId = profile.get('templateId');
      const template = profile.get('waitingForCategories')
        ? profile.get('template')
        : shapeTemplateForApp({
          data: response,
          sections: profile.get('sections'),
          shop,
          shopData,
          templateId,
          userFeatures,
        });

      const operations = getTemplateOperationsForProducts({
        channel,
        profile: profile.set('template', template),
        userFeatures,
      })
        .map(mapOperations);

      if (getSize(operations) === 0) {
        bulkEdit = bulkEdit.delete('processing');
        return state.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
      }

      if (state.hasIn(['bulkEdit', 'products', 'byId'])) {
        updates = applyOperationsToProducts({
          operations,
          products: bulkEdit.get('products').update('edited', updateEdited),
          shop,
          shopData,
          userFeatures,
        });
      }

      if (updates && updates.confirmation) {
        if (!bulkEdit.has('modal')) {
          yield function effect(dispatch) {
            dispatch(
              actions.bulkEdit.setModal({
                action: actions.bulkEdit.setProfile.fulfilled(
                  state.setIn(['bulkEdit', 'modal'], Map({ type: updates.confirmation })),
                  { profile, response },
                ),
                type: updates.confirmation,
              })
            );
          };

          return state;
        } else if (bulkEdit.getIn(['modal', 'type']) === updates.confirmation) {
          bulkEdit = bulkEdit.delete('modal');
        }
      }

      if (bulkEdit.get('vela')) {
        yield function effect(dispatch) {
          dispatch(actions.bulkEdit.saveAs(SAVE_TYPE.SAVE));
        };
      } else {
        switch (channel) {
          case EBAY: {
            if (profile.getIn(['sections', FORM.DETAILS])) {
              const category = template.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);

              if (!state.hasIn(['data', 'loaded', 'taxonomy', channel, marketplaceId, category])) {
                yield function effect(dispatch) {
                  dispatch(actions.data.getTaxonomyData({ category, channel, marketplaceId }));
                };

                return state
                  .setIn(['bulkEdit', 'profile', 'template'], template)
                  .setIn(['bulkEdit', 'profile', 'waitingForCategories'], true)
                  .updateIn(['bulkEdit', 'menu', 'categories'],
                    function updateCategories(categories = EMPTY.SET) {
                      return categories.add(category);
                    }
                  );
              } else if (profile.get('waitingForCategories')) {
                yield function effect(dispatch) {
                  dispatch(actions.bulkEdit.updateMenuForCategory(category));
                };
              }
            }

            break;
          }

          case ETSY: {
            if (profile.getIn(['sections', FORM.DETAILS])) {
              const category = template.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]);

              if (category && category !== AMOUNT.ZERO) {
                yield function effect(dispatch) {
                  dispatch(actions.bulkEdit.updateMenuForCategory(category));
                };
              }
            }

            break;
          }

          default: {
            break;
          }
        }

        if (updates) {
          bulkEdit = bulkEdit
            .delete('processing')
            .setIn(['errors', 'counts'], setErrorCounts({ channel, counts: EMPTY.MAP, products: updates.products }));
        }
      }

      bulkEdit = bulkEdit
        .update('profilesToConnect', function updateProfilesToConnect(profilesToConnect = EMPTY.LIST) {
          return profilesToConnect.push(
            Map({ id: profile.get('id'), listings: selected.toList(), type: profile.get('type') })
          );
        })
        .updateIn(['menu', 'profileUpdates'], function updateProfileUpdates(profileUpdates = EMPTY.SET) {
          return profileUpdates.union(getUpdatedMenuItemsFromOperations({ channel, operations }));
        })
        .set('operations', bulkEdit.get('operations', EMPTY.LIST).concat(operations))
        .set('preview', EMPTY.MAP);

      if (updates) {
        bulkEdit = bulkEdit.set('products', updates.products);
      }

      break;
    }

    default: {
      bulkEdit = bulkEdit.deleteAll(['processing', 'profile']);
      break;
    }
  }

  return state.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* setProfileRejected(state, { error, signal }) {
  if (signal.aborted || getSize(state.get('bulkEdit')) === 0) return state;

  abort(profileController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_PROFILE,
      })
    );
  };

  const bulkEdit = state.get('bulkEdit').deleteAll(['processing', 'profile']);
  const userFeatures = state.getIn(['user', 'features']);

  return state.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* setSelection(reduction, { selection }) {
  let bulkEdit = reduction.get('bulkEdit');
  const channel = bulkEdit.get('channel');
  const menu = bulkEdit.get('menu');
  const products = bulkEdit.get('products');
  const userFeatures = reduction.getIn(['user', 'features']);

  switch (selection) {
    case SELECTION.ALL: {
      if (bulkEdit.get('selection')) {
        bulkEdit = bulkEdit.setIn(['filters', 'offset'], 0);

        if (bulkEdit.getIn(['filters', FILTER[channel].TITLE])) {
          yield function effect(dispatch) {
            dispatch(actions.bulkEdit.getFilterIds(selection));
          };
        } else {
          const ids = getFilterIdsForMenuItem({ menu, products });

          if (ids) {
            if (ids.equals(bulkEdit.getIn(['filters', 'ids']))) {
              yield function effect(dispatch) {
                dispatch(actions.bulkEdit.getProducts(selection));
              };
            } else {
              yield function effect(dispatch) {
                dispatch(actions.bulkEdit.getFilterIds(selection));
              };
              bulkEdit = bulkEdit.setIn(['filters', 'ids'], ids);
            }
          } else {
            yield function effect(dispatch) {
              dispatch(actions.bulkEdit.getFilterIds(selection));
            };
          }
        }
      } else {
        bulkEdit = bulkEdit.setIn(['products', 'selected'], bulkEdit.getIn(['filters', 'ids']).toOrderedSet());
      }

      bulkEdit = bulkEdit.delete('selection');
      break;
    }

    case SELECTION.INCOMPLETE: {
      if (bulkEdit.get('selection') === SELECTION.INCOMPLETE) return reduction;

      bulkEdit = bulkEdit
        .set('selection', SELECTION.INCOMPLETE)
        .setIn(['filters', 'offset'], 0);

      if (bulkEdit.getIn(['filters', FILTER[channel].TITLE])) {
        yield function effect(dispatch) {
          dispatch(actions.bulkEdit.getFilterIds(selection));
        };
      } else {
        const incomplete = bulkEdit.getIn(['errors', 'products']);

        bulkEdit = bulkEdit.setIn(['filters', 'ids'], incomplete);

        yield function effect(dispatch) {
          dispatch(actions.bulkEdit.getProducts({ selection }));
        };
      }

      break;
    }

    case SELECTION.NONE: {
      if (!bulkEdit.get('selection') && !getSize(bulkEdit.getIn(['products', 'selected']))) {
        return reduction;
      }

      if (bulkEdit.get('selection')) {
        bulkEdit = bulkEdit.delete('selection').setIn(['filters', 'offset'], 0);

        yield function effect(dispatch) {
          dispatch(actions.bulkEdit.getFilterIds(selection));
        };

        break;
      }

      bulkEdit = bulkEdit.setIn(['products', 'selected'], EMPTY.ORDERED_SET);
      break;
    }

    case SELECTION.PAGE: {
      const page = bulkEdit.getIn(['products', 'ids']).toOrderedSet();
      const selected = bulkEdit.getIn(['products', 'selected']);
      bulkEdit = bulkEdit.setIn(['products', 'selected'],
        products.get('total') > PAGE_SIZE && getSize(selected) === products.get('total')
          ? page
          : getSize(page.intersect(selected)) === getSize(page)
            ? selected.subtract(page)
            : selected.union(page)
      );
      break;
    }

    default: {
      return reduction;
    }
  }

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* syncUpdates(reduction, { data, route }) {
  if (!data) {
    const userFeatures = reduction.getIn(['user', 'features']);
    let empty = reduction
      .get('bulkEdit')
      .set('syncStarted', true)
      .delete('operations')
      .delete('profilesToConnect')
      .deleteIn(['menu', 'pendingUpdates'])
      .deleteIn(['menu', 'profileUpdates']);

    if (!empty.get('vela')) {
      empty = empty
        .deleteIn(['products', 'byId'])
        .deleteIn(['products', 'edited'])
        .deleteIn(['products', 'ids'])
        .deleteIn(['products', 'total']);
    }

    yield function effect(dispatch) {
      dispatch(actions.bulkEdit.syncUpdates({ data: reduction.get('bulkEdit'), route }));
    };

    return reduction.set('bulkEdit', empty.set('applyDisabled', isApplyDisabled({ bulkEdit: empty, userFeatures })));
  }

  const operations = shapeOperationsForAPI({ operations: data.get('operations') });
  const channel = data.get('channel');
  const shopId = data.get('shopId');
  const db = data.get('db');
  const interval = reduction.getIn(['user', 'config', 'shopsPollingIntervalShort']);
  const profilesToConnect = data.get('profilesToConnect');
  const status = data.get('status');

  yield function effect(dispatch) {
    dispatch(actions.shops.rescheduleShopsPolling(interval || POLLING_INTERVAL.SHORT));

    function reduceProfilesToConnect(result, profile) {
      const listings = profile.get('listings').toArray();
      const profileId = profile.get('id');
      const type = profile.get('type');

      result.requests.push(api.profiles.connectListings({ listings, profileId, type }));

      if (!result.types.has(type)) {
        result.types = result.types.add(type);
      }

      return result;
    }

    function onConnectFulfilled(types) {
      return function onResponse() {
        types.forEach(function forEachType(type) {
          dispatch(actions.data.getProfiles({ force: true, shopId, type }));
        });
      };
    }

    function onConnectRejected(error) {
      console.error(error);
      dispatch(
        actions.notifications.add({
          type: NOTIFICATION.ERROR,
          message: MESSAGE.FAIL.CONNECT_PROFILE,
        })
      );
    }

    if (getSize(profilesToConnect)) {
      const { requests, types } = profilesToConnect.reduce(reduceProfilesToConnect, { requests: [], types: EMPTY.SET });

      Promise.all(requests)
        .then(onConnectFulfilled(types))
        .catch(onConnectRejected);
    }

    function onSynFulfilled() {
      dispatch(actions.bulkEdit.syncUpdates.fulfilled({ route, shopId }));
    }

    function onSyncRejected(error) {
      dispatch(actions.bulkEdit.syncUpdates.rejected({ data, error }));
    }

    api.products.bulkEditProducts({ channel, db, operations, shopId, status })
      .then(onSynFulfilled)
      .catch(onSyncRejected);

    trackAiOperations({ channel, operations, shopId });
  };

  return reduction.deleteIn(['bulkEdit', 'syncStarted']);
}

function* syncUpdatesFulfilled(reduction, { route, shopId }) {
  if (route) {
    yield function effect() {
      navigateTo(route);
    };
  } else if (reduction.getIn(['bulkEdit', 'shopId']) === shopId) {
    return reduction.setIn(['bulkEdit', 'reloadAfterSave'], true);
  }

  return reduction;
}

function* syncUpdatesRejected(reduction, { data, error }) {
  yield function effect(dispatch) {
    console.error('Error encountered while syncing updates: ', error);
    dispatch(actions.notifications.add({ type: NOTIFICATION.ERROR, message: MESSAGE.FAIL.SYNC }));
    dispatch(actions.shops.setData({ path: ['syncData', data.get('shopId')] }));
  };

  return reduction.getIn(['bulkEdit', 'shopId']) === data.get('shopId')
    ? reduction.set('bulkEdit', data)
    : reduction;
}

function* toggleProduct(reduction, id) {
  function toggle(selected) {
    return selected.has(id)
      ? selected.delete(id)
      : selected.add(id);
  }

  const userFeatures = reduction.getIn(['user', 'features']);
  const bulkEdit = reduction
    .get('bulkEdit')
    .updateIn(['products', 'selected'], toggle);

  return reduction.set('bulkEdit', bulkEdit.set('applyDisabled', isApplyDisabled({ bulkEdit, userFeatures })));
}

function* updateMenuForCategory(reduction, category) {
  const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
  const bulkEdit = reduction.get('bulkEdit');
  const channel = bulkEdit.get('channel');
  const shopId = bulkEdit.get('shopId');
  const menu = bulkEdit.get('menu');
  const categories = Set([category]);

  switch (channel) {
    case EBAY: {
      const marketplaceId = reduction.getIn(['shops', 'byId', shopId, 'marketplaceId']);
      return reduction.setIn(['bulkEdit', 'menu'],
        updateMenuConditions({
          categories,
          channel,
          marketplaceId,
          menu: updateMenuTaxonomyAttrubutes({ categories, channel, edited: true, marketplaceId, menu, userFeatures }),
        })
      );
    }

    case ETSY: {
      return reduction.setIn(['bulkEdit', 'menu'],
        updateMenuTaxonomyAttrubutes({ categories, channel, edited: true, menu, userFeatures })
      );
    }

    default: {
      return reduction;
    }
  }
}

function* setTitlePreference(reduction, { titlePreference, shopId, db }) {
  yield function effect(dispatch) {
    api.shops.setTitlePreference({ shopId, preference: titlePreference, db })
      .then(
        (response) => dispatch(actions.bulkEdit.setTitlePreference.fulfilled({ response, shopId, titlePreference })),
        (error) => dispatch(actions.bulkEdit.setTitlePreference.rejected({ error })),
      );
  };

  return reduction.setIn(['bulkEdit', 'rescoring'], true);
}

function* setTitlePreferenceFulfilled(reduction, { response, shopId, titlePreference }) {
  if (!response.success) {
    return reduction.deleteIn(['bulkEdit', 'rescoring']);
  }

  const shop = reduction.getIn(['shops', 'byId', shopId]);
  const userFeatures = reduction.getIn(['user', 'features']);

  function updateProducts(byId) {
    function mapProducts(product) {
      return product.has('score')
        ? updateListingScore({ product, shop, userFeatures })
        : product;
    }

    return byId.map(mapProducts);
  }

  return reduction
    .deleteIn(['bulkEdit', 'rescoring'])
    .updateIn(['bulkEdit', 'products', 'byId'], updateProducts)
    .setIn(['shops', 'byId', shopId, 'titleFormatPreference'], titlePreference);
}

function* setTitlePreferenceRejected(reduction, { error }) {
  if (error) {
    console.error(error);
  }
  return reduction.deleteIn(['bulkEdit', 'rescoring']);
}

const bulkEdit = {
  [actions.bulkEdit.addOperation]: addOperation,
  [actions.bulkEdit.addPendingUpdates]: addPendingUpdates,
  [actions.bulkEdit.addPreview]: addPreview,
  [actions.bulkEdit.aiDiscard]: aiDiscard,
  [actions.bulkEdit.aiOperationGenerate]: aiOperationGenerate,
  [actions.bulkEdit.aiOperationGenerate.fulfilled]: aiOperationGenerateFulfilled,
  [actions.bulkEdit.aiOperationGenerate.rejected]: aiOperationGenerateRejected,
  [actions.bulkEdit.aiPreviewGenerate]: aiPreviewGenerate,
  [actions.bulkEdit.aiPreviewGenerate.fulfilled]: aiPreviewGenerateFulfilled,
  [actions.bulkEdit.aiPreviewGenerate.rejected]: aiPreviewGenerateRejected,
  [actions.bulkEdit.applyPreview]: applyPreview,
  [actions.bulkEdit.bootstrap]: bootstrap,
  [actions.bulkEdit.bootstrap.fulfilled]: bootstrapFulfilled,
  [actions.bulkEdit.bootstrap.rejected]: bootstrapRejected,
  [actions.bulkEdit.closeBulkEdit]: closeBulkEdit,
  [actions.bulkEdit.getCategories]: getCategories,
  [actions.bulkEdit.getCategories.fulfilled]: getCategoriesFulfilled,
  [actions.bulkEdit.getCategories.rejected]: getCategoriesRejected,
  [actions.bulkEdit.getErrorCounts]: getErrorCounts,
  [actions.bulkEdit.getErrorCounts.fulfilled]: getErrorCountsFulfilled,
  [actions.bulkEdit.getErrorCounts.rejected]: getErrorCountsRejected,
  [actions.bulkEdit.getFilterIds]: getFilterIds,
  [actions.bulkEdit.getFilterIds.fulfilled]: getFilterIdsFulfilled,
  [actions.bulkEdit.getFilterIds.rejected]: getFilterIdsRejected,
  [actions.bulkEdit.getInvalidProducts]: getInvalidProducts,
  [actions.bulkEdit.getInvalidProducts.fulfilled]: getInvalidProductsFulfilled,
  [actions.bulkEdit.getInvalidProducts.rejected]: getInvalidProductsRejected,
  [actions.bulkEdit.getProducts]: getProducts,
  [actions.bulkEdit.getProducts.rejected]: getProductsRejected,
  [actions.bulkEdit.getProducts.fulfilled]: getProductsFulfilled,
  [actions.bulkEdit.getProfileListings]: getProfileListings,
  [actions.bulkEdit.getProfileListings.fulfilled]: getProfileListingsFulfilled,
  [actions.bulkEdit.getProfileListings.rejected]: getProfileListingsRejected,
  [actions.bulkEdit.removePreview]: removePreview,
  [actions.bulkEdit.replaceOperation]: replaceOperation,
  [actions.bulkEdit.saveAs]: saveAs,
  [actions.bulkEdit.scheduleUpdates]: scheduleUpdates,
  [actions.bulkEdit.scheduleUpdates.fulfilled]: scheduleUpdatesFulfilled,
  [actions.bulkEdit.scheduleUpdates.rejected]: scheduleUpdatesRejected,
  [actions.bulkEdit.search]: search,
  [actions.bulkEdit.selectOperation]: selectOperation,
  [actions.bulkEdit.selectMenuItem]: selectMenuItem,
  [actions.bulkEdit.setBanner]: setBanner,
  [actions.bulkEdit.setData]: setData,
  [actions.bulkEdit.setModal]: setModal,
  [actions.bulkEdit.setPage]: setPage,
  [actions.bulkEdit.setProduct]: setProduct,
  [actions.bulkEdit.setProfile]: setProfile,
  [actions.bulkEdit.setProfile.fulfilled]: setProfileFulfilled,
  [actions.bulkEdit.setProfile.rejected]: setProfileRejected,
  [actions.bulkEdit.setSelection]: setSelection,
  [actions.bulkEdit.syncUpdates]: syncUpdates,
  [actions.bulkEdit.syncUpdates.fulfilled]: syncUpdatesFulfilled,
  [actions.bulkEdit.syncUpdates.rejected]: syncUpdatesRejected,
  [actions.bulkEdit.toggleProduct]: toggleProduct,
  [actions.bulkEdit.updateMenuForCategory]: updateMenuForCategory,
  [actions.bulkEdit.setTitlePreference]: setTitlePreference,
  [actions.bulkEdit.setTitlePreference.fulfilled]: setTitlePreferenceFulfilled,
  [actions.bulkEdit.setTitlePreference.rejected]: setTitlePreferenceRejected,
};

export default bulkEdit;
